import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { CButton, CCard, CCardBody, CCardTitle, CCardText, CCol, CRow, CImage } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilPhone,
  cilCalendarCheck,
  cilXCircle,
  cilList,
  cilWrapText,
  cilCart,
  cilCalendar,
  cilDiamond,
  cilNotes,
} from '@coreui/icons'
import BadgeComponent from '../../widgets/BadgeComponent'
import { useTranslation } from 'react-i18next'

const gradientCardStyle = {
  background: 'linear-gradient(135deg, #ffffffff, #ddddfdff)',
  color: '#4a4a4a',
}

const RecommendationCardView = ({
  recommendation,
  onDetail = undefined,
  onSupplier = undefined,
  onEshop = undefined,
  onCalendar = undefined,
  onSwitchOff = undefined,
  onIgnore = undefined,
  onDone = undefined,
  onInnovationSuppliers = undefined,
  onInnovationStatus = undefined,
}) => {
  const { t } = useTranslation()

  const truncatedDescription = useMemo(() => {
    const text = (recommendation && recommendation.Description) || ''
    const maxLength = 120
    if (text.length <= maxLength) return text
    return `${text.slice(0, maxLength).trim()}…`
  }, [recommendation])

  if (!recommendation) return null

  const priority = recommendation.Prio

  const riskValue =
    String(recommendation.Damage) === 'null' || recommendation.Damage === null
      ? 'N/A'
      : `${recommendation.Damage}$`

  const dueValue =
    String(recommendation.expecteddate) === '0'
      ? t('risksNotification.fallbackInit', 'to initialize')
      : recommendation.expecteddate

  const handleDetail = () => onDetail?.(recommendation)
  const handleSupplier = () => onSupplier?.(recommendation)
  const handleEshop = () => onEshop?.(recommendation)
  const handleCalendar = () => onCalendar?.(recommendation)
  const handleSwitchOffClick = () => onSwitchOff?.(recommendation)
  const handleIgnoreClick = () => onIgnore?.(recommendation)
  const handleDoneClick = () => onDone?.(recommendation)
  const handleInnovationSuppliers = () => onInnovationSuppliers?.(recommendation)
  const handleInnovationStatus = () => onInnovationStatus?.(recommendation)

  return (
    <CCard
      className="shadow-lg elevation-hover border-0 mb-4"
      color="light"
      style={gradientCardStyle}
    >
      <CCardBody>
        <CRow className="align-items-center g-3">
          <CCol xs={12} sm={3} className="d-flex justify-content-sm-center">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={handleDetail}
              aria-label={`Open reminder ${recommendation.Title || ''}`}
            >
              <CImage
                rounded
                orientation="top"
                loading="lazy"
                src={recommendation.pic_duplicate}
                width={56}
                height={56}
                alt={recommendation.Title || 'Reminder'}
              />
            </button>
          </CCol>
          <CCol xs={12} sm={9}>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <BadgeComponent priority={priority} />
              </div>
              <h6 className="info-navbar__brand-title fw-semibold mb-0">{recommendation.Title}</h6>
              <div className="text-muted small">
                <span className="me-3">
                  <strong>{t('risksMeta.riskLabel', 'Risk')}:</strong> {riskValue}
                </span>
                <span>
                  <strong>{t('risksMeta.dueLabel', 'Due')}:</strong> {dueValue}
                </span>
              </div>
            </div>
          </CCol>
        </CRow>
        <CRow className="mt-3">
          <CCol>
            <CCardText className="text-secondary mb-3" style={{ overflow: 'hidden' }}>
              {truncatedDescription}
            </CCardText>
            <div className="border-top pt-3">
              <p className="text-uppercase text-muted small mb-2">
                {t('risksActions.solve', 'Solve')}
              </p>
              <div className="d-flex flex-wrap gap-2">
                {Number(recommendation.grp_ID) === 1001 && (
                  <CButton
                    color="primary"
                    className="shadow-sm"
                    aria-label={t('suppliers.actions.assign', 'Open innovation suppliers')}
                    onClick={handleInnovationSuppliers}
                  >
                    <CIcon icon={cilDiamond} size="lg" />
                  </CButton>
                )}
                {Number(recommendation.grp_ID) === 1001 && (
                  <CButton
                    color="primary"
                    className="shadow-sm"
                    aria-label={t('suppliers.actions.detail', 'Open innovation project status')}
                    onClick={handleInnovationStatus}
                  >
                    <CIcon icon={cilNotes} size="lg" />
                  </CButton>
                )}
                <CButton
                  color="primary"
                  className="shadow-sm"
                  aria-label={t('suppliers.actions.call', 'Call supplier')}
                  onClick={handleSupplier}
                >
                  <CIcon icon={cilPhone} size="lg" />
                </CButton>
                <CButton
                  color="primary"
                  className="shadow-sm"
                  aria-label={t('eshop.actions.goDashboardAria', 'Open e-shop')}
                  onClick={handleEshop}
                >
                  <CIcon icon={cilCart} size="lg" />
                </CButton>
                <CButton
                  color="primary"
                  className="shadow-sm"
                  aria-label={t('calendar.add', 'Add to calendar')}
                  onClick={handleCalendar}
                >
                  <CIcon icon={cilCalendar} size="lg" />
                </CButton>
                <CButton
                  color="primary"
                  className="shadow-sm"
                  aria-label="Open detail"
                  onClick={handleDetail}
                >
                  <CIcon icon={cilList} size="lg" />
                </CButton>
              </div>
            </div>
            <div className="border-top mt-3 pt-3">
              <p className="text-uppercase text-muted small mb-2">
                {t('risksActions.actions', 'Actions')}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <CButton
                  color="danger"
                  className="shadow-sm"
                  aria-label={t('risks.actions.returnToActive', 'Switch off reminder')}
                  onClick={handleSwitchOffClick}
                >
                  <CIcon icon={cilXCircle} size="lg" />
                </CButton>
                <CButton
                  color="info"
                  className="shadow-sm"
                  aria-label={t('ignoredrisks.actions.returnToActive', 'Ignore reminder')}
                  onClick={handleIgnoreClick}
                >
                  <CIcon icon={cilWrapText} size="lg" />
                </CButton>
                <CButton
                  color="success"
                  className="shadow-sm"
                  aria-label={t('donerisks.actions.returnToActive', 'Mark reminder done')}
                  onClick={handleDoneClick}
                >
                  <CIcon icon={cilCalendarCheck} size="lg" />
                </CButton>
              </div>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  )
}

RecommendationCardView.propTypes = {
  recommendation: PropTypes.shape({
    reminder_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Prio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Title: PropTypes.string,
    Damage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expecteddate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Description: PropTypes.string,
    grp_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplier_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplier_contact_tel: PropTypes.string,
    supplier_name: PropTypes.string,
    pic_duplicate: PropTypes.string,
    period: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duedate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onDetail: PropTypes.func,
  onSupplier: PropTypes.func,
  onEshop: PropTypes.func,
  onCalendar: PropTypes.func,
  onSwitchOff: PropTypes.func,
  onIgnore: PropTypes.func,
  onDone: PropTypes.func,
  onInnovationSuppliers: PropTypes.func,
  onInnovationStatus: PropTypes.func,
}

const RecommendationCard = memo(RecommendationCardView)

const RecommendationSkeleton = memo(function RecommendationSkeleton() {
  return (
    <CCard
      className="shadow-lg elevation-hover border-0 mb-4"
      color="light"
      style={gradientCardStyle}
    >
      <CCardBody>
        <CRow className="g-3">
          <CCol xs={12} sm={3} className="d-flex justify-content-sm-center">
            <div
              className="placeholder bg-secondary bg-opacity-25 rounded"
              style={{ width: 56, height: 56 }}
            />
          </CCol>
          <CCol xs={12} sm={9}>
            <div
              className="placeholder bg-secondary bg-opacity-25 rounded mb-2"
              style={{ width: '60%', height: 18 }}
            />
            <div
              className="placeholder bg-secondary bg-opacity-25 rounded mb-2"
              style={{ width: '80%', height: 18 }}
            />
            <div
              className="placeholder bg-secondary bg-opacity-25 rounded"
              style={{ width: '40%', height: 18 }}
            />
          </CCol>
        </CRow>
        <div
          className="placeholder bg-secondary bg-opacity-25 rounded mt-4"
          style={{ width: '100%', height: 64 }}
        />
      </CCardBody>
    </CCard>
  )
})

export default RecommendationCard
export { RecommendationCardView, RecommendationSkeleton }
